/**
 * CreateTaskPage — supervisor / contractor_admin assigns a new
 * cleaning task to a room.
 *
 * Pre-go-live (2026-05-18). Until this page existed, the only way to
 * create a cleaning task was Supabase Studio.
 *
 * Submit hits the create-cleaning-task Edge Function which:
 *   - validates role / org / room / assignee
 *   - refuses a second active task on the same room
 *   - seeds the checklist items from matching templates
 *   - emits the cleaning_task.created audit row
 *
 * The page does NOT write to cleaning_tasks directly — never bypass
 * the RPC (CLAUDE.md rule 5).
 */

import { useState, type ReactNode, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useOrgCleaners } from '@/hooks/useOrgPickers'
import {
  useCreateCleaningTask,
  type CycleType,
  type ServiceTier,
} from '@/hooks/useCreateCleaningTask'
import { isAppError } from '@/lib/errors/AppError'
import { taskDetailPath, PATHS } from '@/router/paths'
import { RoomDiscoveryPicker, type ClaimedRoomSummary } from '@/components/rooms/RoomDiscoveryPicker'

const CYCLE_TYPES: ReadonlyArray<{ value: CycleType; label: string }> = [
  { value: 'turnover',             label: 'Turnover' },
  { value: 'touchup',              label: 'Touch-up' },
  { value: 'deep_clean',           label: 'Deep clean' },
  { value: 'camp_turn',            label: 'Camp turn' },
  { value: 'maintenance_followup', label: 'Maintenance follow-up' },
]

const SERVICE_TIERS: ReadonlyArray<{ value: ServiceTier; label: string }> = [
  { value: 'touch_up', label: 'Touch up (Line Item 1, 11 items)' },
  { value: 'normal',   label: 'Normal (Line Item 2, 26 items)' },
  { value: 'heavy',    label: 'Heavy (Line Item 3, 29 items)' },
  { value: 'partial',  label: 'Partial unit' },
  { value: 'special',  label: 'Special (one-off scope)' },
]

export function CreateTaskPage(): ReactNode {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create   = useCreateCleaningTask()
  const cleaners = useOrgCleaners()

  const [roomId,                    setRoomId]                    = useState('')
  const [claimedRoom,               setClaimedRoom]               = useState<ClaimedRoomSummary | null>(null)
  const [cycleType,                 setCycleType]                 = useState<CycleType>('turnover')
  const [serviceTier,               setServiceTier]               = useState<ServiceTier>('normal')
  const [assignedToUserId,          setAssignedToUserId]          = useState('')
  const [estimatedMinutes,          setEstimatedMinutes]          = useState('')
  const [isPartialUnit,             setIsPartialUnit]             = useState(false)
  const [kitchenExcluded,           setKitchenExcluded]           = useState(false)
  const [partialScopeDescription,   setPartialScopeDescription]   = useState('')

  // When the supervisor picks the "partial" tier, default the
  // is_partial_unit checkbox to on — these two surfaces always travel
  // together and unchecking the box would have created a contradictory
  // row that the RPC's INVALID_PARTIAL_FLAGS check refuses.
  function handleTierChange(next: ServiceTier): void {
    setServiceTier(next)
    if (next === 'partial' && !isPartialUnit) {
      setIsPartialUnit(true)
    }
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!roomId) return

    const minutesNum = estimatedMinutes.trim().length > 0
      ? Number.parseInt(estimatedMinutes, 10)
      : null

    try {
      const result = await create.mutateAsync({
        roomId,
        cycleType,
        serviceTier,
        assignedToUserId:        assignedToUserId || null,
        estimatedMinutes:        Number.isFinite(minutesNum) && minutesNum !== null && minutesNum > 0
                                   ? minutesNum
                                   : null,
        isPartialUnit:           isPartialUnit,
        kitchenExcluded:         isPartialUnit ? kitchenExcluded : false,
        partialScopeDescription: isPartialUnit && partialScopeDescription.trim().length > 0
                                   ? partialScopeDescription.trim()
                                   : null,
      })
      navigate(taskDetailPath(result.task_id))
    } catch {
      // AppError surfaces inline below via create.error — no toast.
    }
  }

  const errMsg = create.error && isAppError(create.error) ? create.error.userMessage : null

  return (
    <article style={{ maxWidth: '38rem', margin: '0 auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('createTask.title', 'New cleaning task')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)', fontSize: '0.9rem' }}>
          {t(
            'createTask.subtitle',
            "Pick a room and tier. Checklist items are seeded automatically from your contract templates.",
          )}
        </p>
      </header>

      {!claimedRoom && (
        <RoomDiscoveryPicker
          onRoomClaimed={(summary) => {
            setClaimedRoom(summary)
            setRoomId(summary.roomId)
          }}
          disabled={create.isPending}
        />
      )}

      {claimedRoom && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }} aria-busy={create.isPending}>
          <div
            data-testid="ct-claimed-room"
            style={{
              border:        '1px solid var(--brand-neutral-300)',
              borderRadius:  'var(--brand-radius-sm)',
              padding:       '0.6rem 0.75rem',
              display:       'flex',
              alignItems:    'center',
              justifyContent:'space-between',
              gap:           '0.5rem',
            }}
          >
            <span style={{ fontSize: '0.95rem', color: 'var(--brand-neutral-800)' }}>
              <strong>{claimedRoom.buildingName}</strong> · {t('createTask.roomLabel', 'Room')} {claimedRoom.roomNumber}
              <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                ({claimedRoom.bedCount === 1 ? 'single bed' : 'double bed'} · {claimedRoom.bathroomCount === 1 ? 'single bath' : 'double bath'})
              </span>
            </span>
            <button
              type="button"
              data-testid="ct-change-room"
              onClick={() => { setClaimedRoom(null); setRoomId('') }}
              disabled={create.isPending}
              style={{
                background:    'transparent',
                color:         'var(--brand-neutral-700)',
                border:        '1px solid var(--brand-neutral-300)',
                borderRadius:  'var(--brand-radius-sm)',
                padding:       '0.25rem 0.6rem',
                fontSize:      '0.8rem',
                cursor:        'pointer',
              }}
            >
              {t('createTask.changeRoom', 'Change room')}
            </button>
          </div>

          <Field label={t('createTask.cycle', 'Cycle type')}>
          <select
            data-testid="ct-cycle"
            value={cycleType}
            onChange={(e) => setCycleType(e.target.value as CycleType)}
            required
            disabled={create.isPending}
            style={inputStyle}
          >
            {CYCLE_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>

        <Field label={t('createTask.tier', 'Service tier')}>
          <select
            data-testid="ct-tier"
            value={serviceTier}
            onChange={(e) => handleTierChange(e.target.value as ServiceTier)}
            required
            disabled={create.isPending}
            style={inputStyle}
          >
            {SERVICE_TIERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>

        <Field label={t('createTask.assignee', 'Assign to (optional)')}>
          <select
            data-testid="ct-assignee"
            value={assignedToUserId}
            onChange={(e) => setAssignedToUserId(e.target.value)}
            disabled={create.isPending || cleaners.isLoading}
            style={inputStyle}
          >
            <option value="">{t('createTask.unassigned', 'Unassigned — assign later')}</option>
            {(cleaners.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name} ({c.role})
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('createTask.estimatedMinutes', 'Estimated minutes (optional)')}>
          <input
            data-testid="ct-estimate"
            type="number"
            inputMode="numeric"
            min={1}
            max={1440}
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(e.target.value)}
            disabled={create.isPending}
            placeholder="e.g. 45"
            style={inputStyle}
          />
        </Field>

        {isPartialUnit && (
          <fieldset
            data-testid="ct-partial-fieldset"
            style={{
              border:       '1px solid var(--brand-neutral-300)',
              borderRadius: 'var(--brand-radius-sm)',
              padding:      '0.75rem',
              display:      'flex',
              flexDirection:'column',
              gap:          '0.5rem',
            }}
          >
            <legend style={{ padding: '0 0.3rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
              {t('createTask.partialLegend', 'Partial-unit details')}
            </legend>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                data-testid="ct-kitchen-excluded"
                checked={kitchenExcluded}
                onChange={(e) => setKitchenExcluded(e.target.checked)}
                disabled={create.isPending}
              />
              {t('createTask.kitchenExcluded', 'Kitchen excluded')}
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
              {t('createTask.partialScope', 'Scope description (≤500 chars, optional)')}
              <textarea
                data-testid="ct-partial-scope"
                rows={2}
                maxLength={500}
                value={partialScopeDescription}
                onChange={(e) => setPartialScopeDescription(e.target.value.slice(0, 500))}
                disabled={create.isPending}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </label>
          </fieldset>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            data-testid="ct-is-partial"
            checked={isPartialUnit}
            onChange={(e) => {
              setIsPartialUnit(e.target.checked)
              if (!e.target.checked) {
                setKitchenExcluded(false)
                setPartialScopeDescription('')
              }
            }}
            disabled={create.isPending}
          />
          {t('createTask.isPartial', 'This is a partial-unit job')}
        </label>

        {errMsg && (
          <p role="alert" data-testid="ct-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
            {errMsg}
          </p>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="submit"
            data-testid="ct-submit"
            disabled={create.isPending || !roomId}
            style={{
              background:    'var(--brand-primary)',
              color:         'var(--brand-white)',
              border:        'none',
              borderRadius:  'var(--brand-radius-md)',
              padding:       '0.6rem 1.2rem',
              fontSize:      '0.95rem',
              fontWeight:    600,
              cursor:        (create.isPending || !roomId) ? 'default' : 'pointer',
              opacity:       (create.isPending || !roomId) ? 0.6 : 1,
            }}
          >
            {create.isPending ? t('createTask.creating', 'Creating…') : t('createTask.submit', 'Create task')}
          </button>
          <button
            type="button"
            onClick={() => navigate(PATHS.dashboard)}
            data-testid="ct-cancel"
            disabled={create.isPending}
            style={{
              background:    'transparent',
              color:         'var(--brand-neutral-700)',
              border:        '1px solid var(--brand-neutral-300)',
              borderRadius:  'var(--brand-radius-md)',
              padding:       '0.6rem 1.2rem',
              fontSize:      '0.95rem',
              cursor:        'pointer',
            }}
          >
            {t('createTask.cancel', 'Cancel')}
          </button>
        </div>
      </form>
      )}
    </article>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
      {label}
      {children}
    </label>
  )
}

const inputStyle: React.CSSProperties = {
  fontSize:     '0.95rem',
  padding:      '0.45rem 0.55rem',
  borderRadius: 'var(--brand-radius-sm)',
  border:       '1px solid var(--brand-neutral-300)',
  background:   'var(--brand-white)',
}
